import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { LeaderboardApiService } from '../../shared/api/leaderboard-api.service';
import { LeaderboardIdeaDto, LeaderboardUserDto } from '../../shared/api/leaderboard-api.types';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

@Component({
  selector: 'app-leaderboard',
  standalone: true,
  imports: [CommonModule, RouterLink, LoaderComponent],
  templateUrl: './leaderboard.component.html',
  styleUrl: './leaderboard.component.css'
})
export class LeaderboardComponent implements OnInit, OnDestroy {
  activeTab: 'topSubmitters' | 'mostHelpful' | 'topIdeas' | 'deliveredImpact' = 'topSubmitters';

  users: LeaderboardUserDto[] = [];
  ideas: LeaderboardIdeaDto[] = [];

  loading = false;
  errorMessage = '';

  showPointsModal = false;

  private readonly destroy$ = new Subject<void>();

  readonly tabs: { key: LeaderboardComponent['activeTab']; label: string }[] = [
    { key: 'topSubmitters', label: 'Top Submitters' },
    { key: 'mostHelpful', label: 'Most Helpful' },
    { key: 'topIdeas', label: 'Top Ideas' },
    { key: 'deliveredImpact', label: 'Delivered Impact' }
  ];

  constructor(private readonly api: LeaderboardApiService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setTab(tab: LeaderboardComponent['activeTab']): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.users = [];
    this.ideas = [];

    const done = () => (this.loading = false);

    if (this.activeTab === 'topIdeas') {
      this.api
        .getTopIdeas(50)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (items) => {
            this.ideas = items ?? [];
            done();
          },
          error: () => {
            this.errorMessage = 'Failed to load leaderboard.';
            done();
          }
        });
      return;
    }

    this.api
      .getUsers(this.sortParamForTab(this.activeTab), 50)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.users = items ?? [];
          done();
        },
        error: () => {
          this.errorMessage = 'Failed to load leaderboard.';
          done();
        }
      });
  }

  openPointsModal(): void {
    this.showPointsModal = true;
  }

  closePointsModal(): void {
    this.showPointsModal = false;
  }

  initials(name: string | null | undefined): string {
    const n = (name ?? '').trim();
    if (!n) return '?';
    const parts = n.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '?';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
    return (first + last).toUpperCase();
  }

  rankIcon(rank: number | null | undefined): string {
    if (rank === 1) return '🏆';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return String(rank ?? '—');
  }

  avatarEmoji(rank: number | null | undefined): string {
    if (rank === 1) return '😎';
    if (rank === 2) return '🤓';
    if (rank === 3) return '🧑‍💼';
    if ((rank ?? 0) <= 10) return '🧑‍💻';
    return '👤';
  }

  badgeLabel(code: string): string {
    const c = (code ?? '').trim().toUpperCase();
    const map: Record<string, string> = {
      INNOVATOR: 'Innovator',
      PROBLEM_SOLVER: 'Problem Solver',
      COLLABORATOR: 'Collaborator',
      TOP_CONTRIBUTOR: 'Top Contributor'
    };
    return map[c] ?? c;
  }

  primaryPillText(u: LeaderboardUserDto): string {
    if (u.badges?.includes('TOP_CONTRIBUTOR')) return 'Top Contributor';
    if (u.badges?.includes('INNOVATOR')) return 'Innovator';
    if (u.badges?.includes('PROBLEM_SOLVER')) return 'Problem Solver';
    if (u.badges?.includes('COLLABORATOR')) return 'Collaborator';
    return 'Contributor';
  }

  usersEmptyStateMessage(): string {
    switch (this.activeTab) {
      case 'topSubmitters':
        return 'There are no top submitters yet. Once someone earns points from their ideas, they will appear here.';
      case 'mostHelpful':
        return 'No most helpful contributors yet.';
      case 'deliveredImpact':
        return 'No delivered impact to show yet.';
      default:
        return 'No leaderboard entries yet.';
    }
  }

  rowMetricLabel(): string {
    if (this.activeTab === 'mostHelpful') return 'helpful score';
    if (this.activeTab === 'deliveredImpact') return 'delivered ideas';
    return 'ideas';
  }

  userMetricClass(): string {
    return this.activeTab === 'deliveredImpact' ? 'metric-success' : 'metric-primary';
  }

  userPrimaryMetric(u: LeaderboardUserDto): string {
    if (this.activeTab === 'mostHelpful') {
      return `${u.helpfulScore ?? 0} engagement`;
    }

    if (this.activeTab === 'deliveredImpact') {
      return `${u.deliveredIdeas ?? 0} delivered`;
    }

    return `${u.totalPoints ?? 0} points`;
  }

  userSecondaryMetric(u: LeaderboardUserDto): string {
    if (this.activeTab === 'mostHelpful') {
      const comments = u.commentsReceived ?? 0;
      return `${comments} ${comments === 1 ? 'comment' : 'comments'}`;
    }

    if (this.activeTab === 'deliveredImpact') {
      return `of ${u.ideasSubmitted ?? 0} total`;
    }

    const ideas = u.ideasSubmitted ?? 0;
    return `${ideas} ${ideas === 1 ? 'idea' : 'ideas'}`;
  }

  ideaDescription(i: LeaderboardIdeaDto): string {
    const description = (i.description ?? '').trim();
    if (description) {
      return description;
    }

    return 'High-impact idea with measurable value for the team.';
  }

  ideaMetaCategory(i: LeaderboardIdeaDto): string {
    return (i.categoryName ?? '').trim() || 'General';
  }

  statusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Triage',
      3: 'Under Review',
      4: 'Accepted',
      5: 'Rejected',
      6: 'Planned',
      7: 'In Progress',
      8: 'Delivered',
      9: 'Archived',
      10: 'Need more info'
    };
    return labels[status] ?? 'Unknown';
  }

  private sortParamForTab(tab: LeaderboardComponent['activeTab']): string {
    if (tab === 'mostHelpful') return 'mostHelpful';
    if (tab === 'deliveredImpact') return 'deliveredImpact';
    return 'topSubmitters';
  }
}
